import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Avatar,
  Badge,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  useToast,
  useBreakpointValue,
  IconButton,
  Select,
  Textarea,
  Image,
  useColorModeValue,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react'
import {
  MagnifyingGlass,
  ArrowLeft,
  Ticket,
  PaperPlaneRight,
  CheckCircle,
  Plus,
  ChatTeardropDots,
  ShoppingBag,
  ArrowSquareOut,
  Circle,
  Paperclip,
  Download,
  FileDoc,
  Trash,
  List,
  ArrowsLeftRight,
} from '@phosphor-icons/react'
import { useDropzone } from 'react-dropzone'
import { formatDistanceToNow, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { renderMessageWithLinks } from '../../utils/messageUtils'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { VideoThumbnail } from '../../components/VideoPlayer/VideoThumbnail'
import { AudioPlayer } from '../../components/AudioPlayer'
import { useFileUpload } from '../../hooks/useFileUpload'
import { UploadProgressList } from '../../components/UploadProgress'
import {
  TICKET_DROPZONE_ACCEPT,
  TICKET_FILE_INPUT_ACCEPT,
  MAX_TICKET_ATTACHMENTS,
  filesFromClipboard,
  toAttachmentPayload,
  formatFileSize,
  downloadTicketAttachment,
  PendingFileChips,
} from './attachments'
import socketService, { SOCKET_EVENTS } from '../../services/socket'
import { Sidebar } from '../../layouts/DefaultLayout'
import CreateTicketModal from './CreateTicketModal'
import TransferTicketModal from './TransferTicketModal'
import OrderDetailModal from './OrderDetailModal'
import ChatDetailModal from './ChatDetailModal'
import ChannelStatusPanel from './ChannelStatusPanel'
import ConfirmModal from '../../components/ConfirmModal'

export interface InternalTicket {
  id: number
  subject: string
  createdByAgentId: number
  assignedToAgentId: number
  supportChatId: number | null
  orderId: number | null
  status: string
  priority: string
  categoryId: number | null
  categoryName: string | null
  categoryColor: string | null
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
  creatorName: string
  creatorProfilePic: string | null
  assignedName: string
  assignedProfilePic: string | null
}

export interface TicketCategory {
  id: number
  name: string
  color: string
}

interface TicketMessage {
  id: number
  ticketId: number
  senderId: number
  message: string | null
  messageType: string
  fileUrl: string | null
  fileType: string | null
  fileName: string | null
  fileSize: number | null
  statusChange: string | null
  createdAt: string
  senderName: string
  senderProfilePic: string | null
}

interface TicketDetail extends InternalTicket {
  creatorEmail: string
  assignedEmail: string
  messages: TicketMessage[]
  supportChat: any | null
  order: any | null
  categoryId: number | null
  categoryName: string | null
  categoryColor: string | null
}

export const priorityConfig: Record<string, { label: string; color: string; scheme: string }> = {
  low: { label: 'Baixa', color: 'gray.400', scheme: 'gray' },
  medium: { label: 'Média', color: 'blue.400', scheme: 'blue' },
  high: { label: 'Alta', color: 'orange.400', scheme: 'orange' },
  urgent: { label: 'Urgente', color: 'red.400', scheme: 'red' },
}

export const statusConfig: Record<string, { label: string; color: string; scheme: string }> = {
  open: { label: 'Aberto', color: 'yellow.400', scheme: 'yellow' },
  in_progress: { label: 'Em Andamento', color: 'blue.400', scheme: 'blue' },
  waiting_response: { label: 'Aguardando Resposta', color: 'orange.400', scheme: 'orange' },
  resolved: { label: 'Concluído', color: 'green.400', scheme: 'green' },
}

export function TicketItem({
  ticket,
  isSelected,
  onClick,
  currentUserId,
}: {
  ticket: InternalTicket
  isSelected: boolean
  onClick: () => void
  currentUserId: number
}) {
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const hoverBg = useColorModeValue('gray.100', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')

  const pConfig = priorityConfig[ticket.priority] || priorityConfig.medium
  const sConfig = statusConfig[ticket.status] || statusConfig.open
  const isCreator = ticket.createdByAgentId === currentUserId
  const otherName = isCreator ? ticket.assignedName : ticket.creatorName
  const otherPic = isCreator ? ticket.assignedProfilePic : ticket.creatorProfilePic

  return (
    <Box
      p={{ base: 2, md: 3 }}
      borderRadius="lg"
      bg={isSelected ? subtleBg : 'transparent'}
      _hover={{ bg: isSelected ? subtleBg : hoverBg }}
      cursor="pointer"
      onClick={onClick}
      borderLeft="3px solid"
      borderLeftColor={isSelected ? 'yellow.500' : pConfig.color}
      borderBottom="1px solid"
      borderBottomColor={borderColor}
      flexShrink={0}
      transition="background-color 0.2s ease"
    >
      <HStack spacing={{ base: 2, md: 3 }} align="start">
        <Avatar
          size={{ base: 'xs', md: 'sm' }}
          name={otherName}
          src={otherPic || undefined}
          bg="yellow.500"
          color="gray.900"
          flexShrink={0}
        />
        <VStack flex={1} align="start" spacing={0} overflow="hidden" minW={0}>
          <HStack w="100%" justify="space-between" align="start" spacing={2}>
            <Text fontWeight="semibold" color={textPrimary} noOfLines={1} fontSize={{ base: 'xs', md: 'sm' }} flex={1} minW={0}>
              {ticket.subject}
            </Text>
          </HStack>
          <HStack spacing={1} mt={1} flexWrap="wrap" w="100%">
            <Badge colorScheme={pConfig.scheme} fontSize={{ base: '9px', md: '10px' }} borderRadius="full" px={1} noOfLines={1}>
              {pConfig.label}
            </Badge>
            <Badge colorScheme={sConfig.scheme} fontSize={{ base: '9px', md: '10px' }} borderRadius="full" px={1} noOfLines={1}>
              {sConfig.label}
            </Badge>
            {ticket.categoryName && (
              <Badge
                fontSize={{ base: '9px', md: '10px' }}
                borderRadius="full"
                px={1}
                bg={ticket.categoryColor || 'gray.500'}
                color="white"
                noOfLines={1}
              >
                {ticket.categoryName}
              </Badge>
            )}
          </HStack>
          <Text fontSize={{ base: '2xs', md: 'xs' }} color={textMuted} noOfLines={1} mt={1}>
            {isCreator ? `Para: ${otherName}` : `De: ${otherName}`}
          </Text>
          <HStack w="100%" spacing={1} mt={0.5}>
            <Text fontSize={{ base: '2xs', md: 'xs' }} color={textMuted} noOfLines={1}>
              Criado: {formatDistanceToNow(new Date(ticket.createdAt), { addSuffix: false, locale: ptBR })}
            </Text>
            <Text fontSize={{ base: '2xs', md: 'xs' }} color={textMuted}>·</Text>
            <Text fontSize={{ base: '2xs', md: 'xs' }} color={ticket.updatedAt !== ticket.createdAt ? 'yellow.500' : textMuted} noOfLines={1}>
              Resp: {formatDistanceToNow(new Date(ticket.updatedAt), { addSuffix: false, locale: ptBR })}
            </Text>
          </HStack>
        </VStack>
      </HStack>
    </Box>
  )
}

function TicketsList({
  tab,
  onSelectTicket,
  selectedTicketId,
  currentUserId,
  refreshTrigger,
  canManageSystem,
}: {
  tab: string
  onSelectTicket: (ticket: InternalTicket) => void
  selectedTicketId?: number
  currentUserId: number
  refreshTrigger: number
  canManageSystem?: boolean
}) {
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const borderSubtle = useColorModeValue('gray.300', 'gray.600')
  const textMuted = useColorModeValue('gray.500', 'gray.400')

  const [tickets, setTickets] = useState<InternalTicket[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [recipientFilter, setRecipientFilter] = useState('') // 'me' | 'others' | '' (all)
  const [categories, setCategories] = useState<TicketCategory[]>([])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/internal-ticket-categories')
        setCategories(response.data || [])
      } catch (error) {
        console.error('Error fetching categories:', error)
      }
    }
    fetchCategories()
  }, [])

  const fetchTickets = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true)
    try {
      const params: Record<string, string | number | undefined> = {
        tab,
        status: statusFilter || undefined,
        categoryId: categoryFilter || undefined,
        search: debouncedSearch || undefined,
        limit: 50,
      }
      if (canManageSystem && tab === 'all' && recipientFilter) {
        params.recipientFilter = recipientFilter
      }
      const response = await api.get('/internal-tickets', { params })
      setTickets(response.data.data)
    } catch (error) {
      if (!silent) console.error('Error fetching internal tickets:', error)
    } finally {
      if (!silent) setIsLoading(false)
    }
  }, [tab, statusFilter, categoryFilter, debouncedSearch, recipientFilter, canManageSystem, refreshTrigger])

  useEffect(() => {
    fetchTickets()
  }, [fetchTickets])

  useEffect(() => {
    const pollInterval = setInterval(() => fetchTickets(true), 90000)
    return () => clearInterval(pollInterval)
  }, [fetchTickets])

  if (isLoading) {
    return (
      <Flex justify="center" p={8}>
        <Spinner size="lg" color="yellow.500" />
      </Flex>
    )
  }

  return (
    <VStack spacing={2} align="stretch" h="100%" overflow="hidden">
      <Box px={{ base: 1, md: 2 }} pt={2} flexShrink={0}>
        <VStack spacing={2}>
          <InputGroup size={{ base: 'xs', md: 'sm' }}>
            <InputLeftElement>
              <MagnifyingGlass size={16} color="gray" />
            </InputLeftElement>
            <Input
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              variant="outline"
              fontSize={{ base: 'xs', md: 'sm' }}
            />
          </InputGroup>
          <Select
            size={{ base: 'xs', md: 'sm' }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            bg={subtleBg}
            borderColor={borderSubtle}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            <option value="">Todos os status</option>
            <option value="open">Aberto</option>
            <option value="in_progress">Em Andamento</option>
            <option value="waiting_response">Aguardando Resposta</option>
            <option value="resolved">Concluído</option>
          </Select>
          <Select
            size={{ base: 'xs', md: 'sm' }}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            bg={subtleBg}
            borderColor={borderSubtle}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            <option value="">Todas as categorias</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </Select>
          {canManageSystem && tab === 'all' && (
            <Select
              size={{ base: 'xs', md: 'sm' }}
              value={recipientFilter}
              onChange={(e) => setRecipientFilter(e.target.value)}
              bg={subtleBg}
              borderColor={borderSubtle}
              fontSize={{ base: 'xs', md: 'sm' }}
              placeholder="Destinatário"
            >
              <option value="">Todos</option>
              <option value="me">Para mim</option>
              <option value="others">Para outros</option>
            </Select>
          )}
        </VStack>
      </Box>
      <VStack spacing={1} align="stretch" overflowY="auto" flex={1} minH={0} px={2} pb={2}>
        {tickets.length === 0 ? (
          <Flex direction="column" align="center" justify="center" py={8}>
            <Ticket size={40} color="#4A5568" weight="duotone" />
            <Text color={textMuted} textAlign="center" mt={3} fontSize="sm">
              Nenhum ticket encontrado
            </Text>
          </Flex>
        ) : (
          tickets.map((ticket) => (
            <TicketItem
              key={ticket.id}
              ticket={ticket}
              isSelected={ticket.id === selectedTicketId}
              onClick={() => onSelectTicket(ticket)}
              currentUserId={currentUserId}
            />
          ))
        )}
      </VStack>
    </VStack>
  )
}

export function TicketDetailView({
  ticketId,
  currentUserId,
  onBack,
  onTicketUpdated,
}: {
  ticketId: number
  currentUserId: number
  onBack: () => void
  onTicketUpdated: () => void
}) {
  const [ticket, setTicket] = useState<TicketDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [replyText, setReplyText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [isResolving, setIsResolving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showOrderModal, setShowOrderModal] = useState(false)
  const [showChatModal, setShowChatModal] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const { canManageSystem } = useAuth()
  const { uploadFileDetailed, uploads, cancelUpload, clearUpload } = useFileUpload()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const toast = useToast()
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const borderSubtle = useColorModeValue('gray.300', 'gray.600')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')

  const addPendingFiles = useCallback((files: File[]) => {
    if (files.length === 0) return
    setPendingFiles((prev) => {
      const next = [...prev, ...files]
      if (next.length > MAX_TICKET_ATTACHMENTS) {
        toast({
          title: `Máximo de ${MAX_TICKET_ATTACHMENTS} arquivos por envio`,
          status: 'warning',
          duration: 3000,
        })
        return next.slice(0, MAX_TICKET_ATTACHMENTS)
      }
      return next
    })
  }, [toast])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: addPendingFiles,
    accept: TICKET_DROPZONE_ACCEPT,
    multiple: true,
    noClick: true,
  })

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = filesFromClipboard(e)
    if (files.length > 0) {
      e.preventDefault()
      addPendingFiles(files)
    }
  }, [addPendingFiles])

  const handleDownload = async (messageId: number, fileName: string | null) => {
    try {
      await downloadTicketAttachment(messageId, fileName)
    } catch {
      toast({ title: 'Erro ao baixar arquivo', status: 'error', duration: 4000 })
    }
  }

  const fetchTicket = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true)
    try {
      const response = await api.get(`/internal-tickets/${ticketId}`)
      setTicket(response.data)
    } catch (error: any) {
      if (!silent) {
        console.error('Error fetching ticket:', error)
        if (error.response?.status === 403) {
          toast({ title: 'Acesso negado', status: 'error', duration: 3000 })
          onBack()
        }
      }
    } finally {
      if (!silent) setIsLoading(false)
    }
  }, [ticketId])

  useEffect(() => {
    fetchTicket()
  }, [fetchTicket])

  useEffect(() => {
    const pollInterval = setInterval(() => fetchTicket(true), 90000)
    return () => clearInterval(pollInterval)
  }, [fetchTicket])

  useEffect(() => {
    const handleReply = (data: any) => {
      if (data.ticketId === ticketId && data.senderId !== currentUserId) {
        setTicket((prev) => {
          if (!prev) return prev
          if (data.id && prev.messages.some((m) => m.id === data.id)) return prev
          return {
            ...prev,
            status: data.newStatus || prev.status,
            messages: [
              ...prev.messages,
              {
                id: data.id || Date.now(),
                ticketId: data.ticketId,
                senderId: data.senderId,
                message: data.message,
                messageType: data.messageType || 'text',
                fileUrl: data.fileUrl || null,
                fileType: data.fileType || null,
                fileName: data.fileName || null,
                fileSize: data.fileSize || null,
                statusChange: data.statusChange,
                createdAt: data.createdAt,
                senderName: data.senderName,
                senderProfilePic: data.senderProfilePic,
              },
            ],
          }
        })
      }
    }

    const handleResolved = (data: any) => {
      if (data.ticketId === ticketId) {
        fetchTicket()
      }
    }

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)

    return () => {
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    }
  }, [ticketId, fetchTicket])

  // Sobe os arquivos para o S3 e manda UMA resposta com todos os anexos;
  // o texto vira legenda do primeiro (antes ia repetido em cada arquivo).
  const handleSendReply = async () => {
    const text = replyText.trim()
    if (!text && pendingFiles.length === 0) return
    if (isSending) return

    setIsSending(true)
    let attachments: ReturnType<typeof toAttachmentPayload>[] = []
    try {
      const uploaded = await Promise.all(pendingFiles.map((file) => uploadFileDetailed(file)))
      attachments = uploaded.map(toAttachmentPayload)
    } catch {
      toast({ title: 'Erro ao enviar arquivo', description: 'Nada foi enviado; tente de novo.', status: 'error', duration: 5000 })
      setIsSending(false)
      return
    }

    try {
      const response = await api.post(`/internal-tickets/${ticketId}/reply`, {
        message: text || null,
        attachments,
      })
      const novas = response.data.messages || [response.data.data]
      setTicket((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          status: response.data.newStatus,
          messages: [...prev.messages, ...novas],
        }
      })
      setReplyText('')
      setPendingFiles([])
      onTicketUpdated()
    } catch (error: any) {
      toast({
        title: 'Erro ao enviar resposta',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsSending(false)
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    addPendingFiles(Array.from(files))
    e.target.value = ''
  }

  const handleResolve = async () => {
    if (isResolving) return
    setIsResolving(true)
    try {
      await api.post(`/internal-tickets/${ticketId}/resolve`)
      toast({ title: 'Ticket concluído', status: 'success', duration: 3000 })
      fetchTicket()
      onTicketUpdated()
    } catch (error: any) {
      toast({
        title: 'Erro ao concluir ticket',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsResolving(false)
    }
  }

  const handleDelete = async () => {
    if (isDeleting) return
    setIsDeleting(true)
    try {
      await api.delete(`/internal-tickets/${ticketId}`)
      toast({ title: 'Ticket excluído', status: 'success', duration: 3000 })
      onTicketUpdated()
      onBack()
    } catch (error: any) {
      toast({
        title: 'Erro ao excluir ticket',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading || !ticket) {
    return (
      <Flex flex={1} align="center" justify="center">
        <Spinner size="lg" color="yellow.500" />
      </Flex>
    )
  }

  const pConfig = priorityConfig[ticket.priority] || priorityConfig.medium
  const sConfig = statusConfig[ticket.status] || statusConfig.open
  const isResolved = ticket.status === 'resolved'

  return (
    <Flex direction="column" h="100%" overflow="hidden">
      {/* Header */}
      <Box p={{ base: 3, md: 4 }} borderBottom="1px" borderColor={borderColor} flexShrink={0}>
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between" spacing={2}>
            <HStack spacing={2} flex={1} minW={0}>
              <IconButton
                aria-label="Voltar"
                icon={<ArrowLeft size={22} />}
                variant="ghost"
                size="md"
                ml={-2}
                onClick={onBack}
                display={{ base: 'flex', lg: 'none' }}
                flexShrink={0}
              />
              <Text fontWeight="bold" color={textPrimary} fontSize={{ base: 'sm', md: 'lg' }} noOfLines={2} flex={1} minW={0}>
                {ticket.subject}
              </Text>
            </HStack>
            <HStack spacing={1} flexShrink={0}>
              {canManageSystem && (
                <IconButton
                  aria-label="Excluir ticket"
                  icon={<Trash size={16} />}
                  size={{ base: 'xs', md: 'sm' }}
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => setShowDeleteConfirm(true)}
                  isLoading={isDeleting}
                />
              )}
              {!isResolved && (ticket.assignedToAgentId === currentUserId || canManageSystem) && (
                <Button
                  size={{ base: 'xs', md: 'sm' }}
                  variant="outline"
                  colorScheme="yellow"
                  leftIcon={<ArrowsLeftRight size={16} />}
                  onClick={() => setShowTransferModal(true)}
                >
                  <Text display={{ base: 'none', sm: 'block' }}>Transferir</Text>
                </Button>
              )}
              {!isResolved && (
                <Button
                  size={{ base: 'xs', md: 'sm' }}
                  colorScheme="green"
                  leftIcon={<CheckCircle size={16} />}
                  onClick={handleResolve}
                  isLoading={isResolving}
                >
                  <Text display={{ base: 'none', sm: 'block' }}>Concluir</Text>
                </Button>
              )}
            </HStack>
          </HStack>

        <HStack spacing={2} flexWrap="wrap">
          <Badge colorScheme={pConfig.scheme} fontSize={{ base: '2xs', md: 'xs' }}>{pConfig.label}</Badge>
          <Badge colorScheme={sConfig.scheme} fontSize={{ base: '2xs', md: 'xs' }}>{sConfig.label}</Badge>
          {ticket.categoryName && (
            <Badge
              bg={ticket.categoryColor || 'gray.500'}
              color="white"
              fontSize={{ base: '2xs', md: 'xs' }}
            >
              {ticket.categoryName}
            </Badge>
          )}
          <Text fontSize={{ base: '2xs', md: 'xs' }} color={textMuted} noOfLines={1}>
            De: <strong>{ticket.creatorName}</strong> → Para: <strong>{ticket.assignedName}</strong>
          </Text>
        </HStack>

        <HStack spacing={2} mt={2} flexWrap="wrap">
          {ticket.supportChatId && (
            <Button
              size={{ base: '2xs', md: 'xs' }}
              variant="outline"
              colorScheme="blue"
              leftIcon={<ChatTeardropDots size={14} />}
              rightIcon={<ArrowSquareOut size={12} />}
              onClick={() => setShowChatModal(true)}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Atendimento #{ticket.supportChatId}</Text>
              <Text display={{ base: 'block', sm: 'none' }}>#{ticket.supportChatId}</Text>
            </Button>
          )}
          {ticket.orderId && (
            <Button
              size={{ base: '2xs', md: 'xs' }}
              variant="outline"
              colorScheme="orange"
              leftIcon={<ShoppingBag size={14} />}
              rightIcon={<ArrowSquareOut size={12} />}
              onClick={() => setShowOrderModal(true)}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Pedido #{ticket.orderId}</Text>
              <Text display={{ base: 'block', sm: 'none' }}>#{ticket.orderId}</Text>
            </Button>
          )}
        </HStack>
        </VStack>
      </Box>

      {/* Messages */}
      <VStack
        flex={1}
        overflowY="auto"
        p={{ base: 2, md: 4 }}
        spacing={3}
        align="stretch"
        minH={0}
        position="relative"
        {...getRootProps({ onClick: (e: any) => e.stopPropagation() })}
      >
        <input {...getInputProps()} />
        {(ticket.orderId || ticket.supportChatId) && <ChannelStatusPanel ticketId={ticket.id} />}
        {isDragActive && (
          <Flex
            position="absolute"
            inset={0}
            bg="blackAlpha.700"
            zIndex={10}
            align="center"
            justify="center"
            borderRadius="md"
          >
            <VStack>
              <Paperclip size={40} color="#ECC94B" />
              <Text color="white" fontWeight="bold">Solte os arquivos aqui</Text>
            </VStack>
          </Flex>
        )}
        {ticket.messages.map((msg) => {
          const msgText = msg.message || ''
          const isTransferMessage = msgText.startsWith('Transferido de')
          if (isTransferMessage) {
            return (
              <Flex key={msg.id} justify="center">
                <Text
                  fontSize="xs"
                  color={textMuted}
                  bg={subtleBg}
                  px={3}
                  py={2}
                  borderRadius="lg"
                  textAlign="center"
                  maxW="90%"
                >
                  {msgText}
                </Text>
              </Flex>
            )
          }
          if (msg.statusChange && !msgText.trim() && !msg.fileUrl) {
            return (
              <Flex key={msg.id} justify="center">
                <Text
                  fontSize="xs"
                  color={textMuted}
                  bg={subtleBg}
                  px={3}
                  py={1}
                  borderRadius="full"
                  textAlign="center"
                >
                  Status alterado para: {statusConfig[msg.statusChange]?.label || msg.statusChange}
                </Text>
              </Flex>
            )
          }

          const isMe = msg.senderId === currentUserId
          return (
            <Flex key={msg.id} direction="column">
              {msg.statusChange && (
                <Flex justify="center" mb={1}>
                  <HStack spacing={1}>
                    <Circle size={8} color={statusConfig[msg.statusChange]?.color || '#A0AEC0'} weight="fill" />
                    <Text fontSize="2xs" color={textMuted}>
                      Status → {statusConfig[msg.statusChange]?.label || msg.statusChange}
                    </Text>
                  </HStack>
                </Flex>
              )}
              <Flex justify={isMe ? 'flex-end' : 'flex-start'} px={1}>
                <Box
                  maxW={{ base: '85%', md: '80%' }}
                  bg={isMe ? 'yellow.600' : subtleBg}
                  color="white"
                  px={{ base: 2, md: 3 }}
                  py={{ base: 1.5, md: 2 }}
                  borderRadius="lg"
                  borderTopRightRadius={isMe ? '2px' : 'lg'}
                  borderTopLeftRadius={isMe ? 'lg' : '2px'}
                  minW={0}
                >
                  <Text fontSize="xs" fontWeight="bold" mb={1} color={isMe ? 'yellow.100' : 'blue.300'}>
                    {msg.senderName || 'Atendente'}
                  </Text>
                  {msg.messageType === 'image' && msg.fileUrl && (
                    <VStack align={isMe ? 'end' : 'start'} spacing={1} mb={1}>
                      <Image
                        src={msg.fileUrl}
                        alt={msg.fileName || 'Imagem'}
                        maxH="300px"
                        maxW="300px"
                        borderRadius="lg"
                        cursor="pointer"
                        onClick={() => window.open(msg.fileUrl!, '_blank')}
                      />
                    </VStack>
                  )}
                  {msg.messageType === 'video' && msg.fileUrl && (
                    <VStack align={isMe ? 'end' : 'start'} spacing={1} mb={1}>
                      <VideoThumbnail src={msg.fileUrl} maxWidth="280px" maxHeight="200px" isOwn={isMe} />
                    </VStack>
                  )}
                  {msg.messageType === 'audio' && msg.fileUrl && (
                    <Box mb={1}>
                      <AudioPlayer src={msg.fileUrl} isOwn={isMe} />
                    </Box>
                  )}
                  {msg.messageType === 'file' && msg.fileUrl && (
                    <HStack
                      p={3}
                      bg={isMe ? 'yellow.700' : 'gray.600'}
                      borderRadius="lg"
                      spacing={3}
                      mb={1}
                      cursor="pointer"
                      onClick={() => handleDownload(msg.id, msg.fileName)}
                    >
                      <FileDoc size={32} />
                      <VStack align="start" spacing={0} flex={1} minW={0}>
                        <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                          {msg.fileName || 'Arquivo'}
                        </Text>
                        <Text fontSize="xs" color={textMuted}>
                          {formatFileSize(msg.fileSize)}
                        </Text>
                      </VStack>
                      <Download size={20} />
                    </HStack>
                  )}
                  {msgText && (
                    <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
                      {renderMessageWithLinks(msgText)}
                    </Text>
                  )}
                  <Text fontSize="2xs" color={isMe ? 'yellow.200' : 'gray.500'} textAlign="right" mt={1}>
                    {format(new Date(msg.createdAt), 'dd/MM/yy HH:mm', { locale: ptBR })}
                  </Text>
                </Box>
              </Flex>
            </Flex>
          )
        })}
      </VStack>

      {/* Reply area */}
      {!isResolved && (
        <Box flexShrink={0}>
          {pendingFiles.length > 0 && (
            <HStack
              px={3}
              py={2}
              borderTop="1px"
              borderColor={borderColor}
              bg={surfaceBg}
              flexWrap="wrap"
              gap={2}
            >
              <PendingFileChips
                files={pendingFiles}
                bg={subtleBg}
                isDisabled={isSending}
                onRemove={(index) => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
              />
            </HStack>
          )}

          {uploads.size > 0 && (
            <Box px={3} py={2} borderTop="1px" borderColor={borderColor} bg={surfaceBg}>
              <UploadProgressList uploads={uploads} onCancel={cancelUpload} onDismiss={clearUpload} />
            </Box>
          )}

          <Box p={{ base: 2, md: 3 }} borderTop="1px" borderColor={borderColor}>
            <HStack spacing={{ base: 1, md: 2 }}>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept={TICKET_FILE_INPUT_ACCEPT}
                multiple
                onChange={handleFileInputChange}
              />
              <IconButton
                aria-label="Anexar arquivo"
                icon={<Paperclip size={20} />}
                variant="ghost"
                color={textMuted}
                _hover={{ color: textPrimary }}
                onClick={() => fileInputRef.current?.click()}
                isDisabled={isSending}
                size={{ base: 'sm', md: 'md' }}
                flexShrink={0}
              />
              <Textarea
                ref={textareaRef}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onPaste={handlePaste}
                placeholder="Escreva sua resposta..."
                size={{ base: 'xs', md: 'sm' }}
                bg={subtleBg}
                borderColor={borderSubtle}
                resize="none"
                rows={2}
                fontSize={{ base: 'sm', md: 'md' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSendReply()
                  }
                }}
                flex={1}
                minW={0}
              />
              <IconButton
                aria-label="Enviar"
                icon={<PaperPlaneRight size={20} />}
                colorScheme="yellow"
                onClick={handleSendReply}
                isLoading={isSending}
                isDisabled={!replyText.trim() && pendingFiles.length === 0}
                size={{ base: 'sm', md: 'md' }}
                flexShrink={0}
              />
            </HStack>
          </Box>
        </Box>
      )}

      {isResolved && (
        <Box p={3} borderTop="1px" borderColor={borderColor} flexShrink={0} textAlign="center">
          <HStack justify="center" spacing={2}>
            <CheckCircle size={18} color="#48BB78" weight="fill" />
            <Text fontSize="sm" color="green.400">
              Ticket concluído em{' '}
              {ticket.resolvedAt
                ? format(new Date(ticket.resolvedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                : '—'}
            </Text>
          </HStack>
        </Box>
      )}

      {/* Modals */}
      {ticket.order && (
        <OrderDetailModal
          isOpen={showOrderModal}
          onClose={() => setShowOrderModal(false)}
          order={ticket.order}
        />
      )}
      {ticket.supportChat && (
        <ChatDetailModal
          isOpen={showChatModal}
          onClose={() => setShowChatModal(false)}
          chat={ticket.supportChat}
          chatId={ticket.supportChatId!}
        />
      )}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Excluir Ticket"
        message="Tem certeza que deseja excluir este ticket? Todas as mensagens serão perdidas. Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        isLoading={isDeleting}
      />
      <TransferTicketModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        ticketId={ticketId}
        currentAssignedToAgentId={ticket.assignedToAgentId}
        onTransferred={() => {
          fetchTicket()
          onTicketUpdated()
        }}
      />
    </Flex>
  )
}

export default function InternalTickets() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, canManageSystem } = useAuth()
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const pageBg = useColorModeValue('gray.50', 'gray.900')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(id ? Number(id) : null)
  const [activeTab, setActiveTab] = useState(0)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [internalTicketsBadge, setInternalTicketsBadge] = useState(0)
  const { isOpen: isMenuOpen, onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure()
  
  const menuIconColor = useColorModeValue('gray.700', 'white')
  const currentUserId = user?.id || 0

  const fetchInternalTicketsCount = useCallback(async () => {
    try {
      const res = await api.get('/internal-tickets/my-pending-count')
      setInternalTicketsBadge(res.data.count || 0)
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    if (id) {
      setSelectedTicketId(Number(id))
    }
  }, [id])

  useEffect(() => {
    fetchInternalTicketsCount()
    const interval = setInterval(fetchInternalTicketsCount, 120000)

    const handleNew = () => fetchInternalTicketsCount()
    const handleReply = () => fetchInternalTicketsCount()
    const handleResolved = () => fetchInternalTicketsCount()
    const handleTransferred = () => fetchInternalTicketsCount()

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.TRANSFERRED, handleTransferred)

    return () => {
      clearInterval(interval)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.TRANSFERRED, handleTransferred)
    }
  }, [fetchInternalTicketsCount])

  useEffect(() => {
    const handleNewTicket = (data: any) => {
      toast({
        title: 'Novo ticket interno',
        description: `${data.creatorName}: ${data.subject}`,
        status: 'info',
        duration: 5000,
        position: 'top-right',
      })
      setRefreshTrigger((prev) => prev + 1)
    }

    const handleReply = () => {
      setRefreshTrigger((prev) => prev + 1)
    }

    const handleResolved = () => {
      setRefreshTrigger((prev) => prev + 1)
    }

    const handleTransferred = (data: any) => {
      toast({
        title: 'Ticket transferido',
        description: data.subject ? `Ticket "${data.subject}" foi transferido para você` : 'Um ticket foi transferido para você',
        status: 'info',
        duration: 5000,
        position: 'top-right',
      })
      setRefreshTrigger((prev) => prev + 1)
    }

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNewTicket)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.TRANSFERRED, handleTransferred)

    return () => {
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNewTicket)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.TRANSFERRED, handleTransferred)
    }
  }, [toast])

  const handleSelectTicket = (ticket: InternalTicket) => {
    setSelectedTicketId(ticket.id)
    navigate(`/internal-tickets/${ticket.id}`)
  }

  const handleBack = () => {
    setSelectedTicketId(null)
    navigate('/internal-tickets')
  }

  const handleTicketCreated = () => {
    setIsCreateOpen(false)
    setRefreshTrigger((prev) => prev + 1)
  }

  const tabMap = ['all', 'created', 'assigned']
  const showSidebar = !isMobile || !selectedTicketId

  return (
    <>
      {/* Mobile menu drawer */}
      {isMobile && (
        <Drawer isOpen={isMenuOpen} placement="left" onClose={onMenuClose}>
          <DrawerOverlay />
          <DrawerContent bg={surfaceBg} maxW="280px">
            <DrawerCloseButton />
            <DrawerBody p={0}>
              <Sidebar onClose={onMenuClose} internalTicketsBadge={internalTicketsBadge} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      <Flex h="100%" overflow="hidden" direction={{ base: 'column', lg: 'row' }}>
        {/* Sidebar */}
      {showSidebar && (
        <Box
          w={{ base: '100%', lg: '350px' }}
          minW={{ base: '100%', lg: '300px', xl: '350px' }}
          maxW={{ base: '100%', lg: '400px' }}
          bg={surfaceBg}
          borderRight={{ base: 'none', lg: '1px' }}
          borderBottom={{ base: '1px', lg: 'none' }}
          borderColor={borderColor}
          overflow="hidden"
          h="100%"
          minH={0}
          flexShrink={0}
        >
          <VStack h="100%" spacing={0}>
            <HStack w="100%" p={{ base: 3, md: 4 }} borderBottom="1px" borderColor={borderColor} justify="space-between" spacing={2}>
              <HStack spacing={3} align="center" flex={1} minW={0}>
                {isMobile && (
                  <IconButton
                    aria-label="Menu"
                    icon={<List size={20} />}
                    variant="ghost"
                    onClick={onMenuOpen}
                    size="sm"
                    color={menuIconColor}
                    _hover={{ bg: borderColor }}
                    flexShrink={0}
                  />
                )}
                <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color={textPrimary}>
                  Tickets Internos
                </Text>
              </HStack>
              <Button
                size={{ base: 'xs', md: 'sm' }}
                leftIcon={<Plus size={14} weight="bold" />}
                bg="yellow.500"
                color="gray.900"
                _hover={{ bg: 'yellow.400' }}
                onClick={() => setIsCreateOpen(true)}
                flexShrink={0}
              >
                <Text display={{ base: 'none', sm: 'block' }}>Novo</Text>
              </Button>
            </HStack>
            <Tabs
              w="100%"
              flex={1}
              display="flex"
              flexDirection="column"
              variant="soft-rounded"
              colorScheme="yellow"
              size="xs"
              overflow="hidden"
              index={activeTab}
              onChange={(index) => setActiveTab(index)}
            >
              <TabList px={{ base: 1, md: 2 }} pt={3} gap={1} flexWrap="nowrap" justifyContent="center" overflowX="auto">
                <Tab px={{ base: 1, sm: 2 }} py={1} fontSize="xs" flexShrink={0}>Todos</Tab>
                <Tab px={{ base: 1, sm: 2 }} py={1} fontSize="xs" flexShrink={0}>Criados</Tab>
                <Tab px={{ base: 1, sm: 2 }} py={1} fontSize="xs" flexShrink={0}>Recebidos</Tab>
              </TabList>
              {canManageSystem && activeTab === 0 && (
                <Text fontSize="xs" color="yellow.500" textAlign="center" px={2} pt={1}>
                  Visualizando todos os tickets do sistema
                </Text>
              )}
              <TabPanels flex={1} overflow="hidden" mt={3} minH={0}>
                {tabMap.map((tabKey, idx) => (
                  <TabPanel key={tabKey} h="100%" p={0} overflow="hidden">
                    {activeTab === idx && (
                      <TicketsList
                        tab={tabKey}
                        onSelectTicket={handleSelectTicket}
                        selectedTicketId={selectedTicketId || undefined}
                        currentUserId={currentUserId}
                        refreshTrigger={refreshTrigger}
                        canManageSystem={canManageSystem}
                      />
                    )}
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </VStack>
        </Box>
      )}

      {/* Detail area */}
      {selectedTicketId ? (
        <Box flex={1} overflow="hidden" bg={pageBg} minW={0}>
          <TicketDetailView
            ticketId={selectedTicketId}
            currentUserId={currentUserId}
            onBack={handleBack}
            onTicketUpdated={() => setRefreshTrigger((prev) => prev + 1)}
          />
        </Box>
      ) : (
        !isMobile && (
          <Flex flex={1} align="center" justify="center" bg={pageBg} minW={0}>
            <VStack spacing={4} px={4}>
              <Ticket size={64} color="#4A5568" weight="duotone" />
              <Text color={textMuted} fontSize="lg" textAlign="center">
                Selecione um ticket para visualizar
              </Text>
            </VStack>
          </Flex>
        )
      )}

      {/* Create modal */}
      <CreateTicketModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={handleTicketCreated}
      />
    </Flex>
    </>
  )
}
